import { useEffect, useMemo, useState } from "react";

import PageHeader from "../components/PageHeader.jsx";
import apiClient from "../services/api.js";

const emptyForm = { item_name: "Daily contribution", quantity: 1, contribution_amount: "", note: "" };

const formatMoney = (value) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return "Recently added";
  return new Intl.DateTimeFormat("en-NG", { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
};

export default function ContributionsPage() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [receipt, setReceipt] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadEntries = async () => {
    const { data } = await apiClient.get("/contributions/");
    setEntries(data || []);
  };

  useEffect(() => {
    loadEntries().catch(() => setEntries([])).finally(() => setLoading(false));
  }, []);

  const total = useMemo(
    () => entries.reduce((sum, entry) => sum + Number(entry.contribution_amount || 0), 0),
    [entries]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("");
    setError("");
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("item_name", form.item_name || "Daily contribution");
      formData.append("quantity", form.quantity || 1);
      formData.append("contribution_amount", form.contribution_amount);
      if (form.note) formData.append("note", form.note);
      if (receipt) formData.append("receipt", receipt);
      await apiClient.post("/contributions/", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setStatus("Your contribution has been recorded.");
      setForm(emptyForm);
      setReceipt(null);
      setShowForm(false);
      await loadEntries();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to save this contribution. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 fade-up md:space-y-7">
      <PageHeader
        title="Daily contributions"
        description="Build consistency by recording each cooperative contribution."
        action={
          <button type="button" onClick={() => setShowForm((current) => !current)} className="rounded-2xl bg-accent px-4 py-2.5 text-xs font-semibold text-white shadow-sm">
            {showForm ? "Close" : "+ Add"}
          </button>
        }
      />

      <section className="grid grid-cols-2 gap-3 md:gap-4">
        <article className="rounded-3xl bg-accent p-4 text-white shadow-lg shadow-accent/10 md:p-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">Total contributed</p>
          <p className="mt-2 text-xl font-bold md:text-3xl">{formatMoney(total)}</p>
          <p className="mt-2 text-xs text-white/65">Across all entries</p>
        </article>
        <article className="card-surface rounded-3xl p-4 md:p-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Entries</p>
          <p className="mt-2 text-2xl font-bold text-ink md:text-3xl">{entries.length}</p>
          <p className="mt-2 text-xs text-slate-500">Contribution records</p>
        </article>
      </section>

      {status && <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{status}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="card-surface space-y-5 rounded-[28px] p-5 md:p-7">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">New contribution</p>
            <h2 className="mt-1 text-2xl font-semibold">Record payment</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-ink">Contribution type
              <input name="item_name" value={form.item_name} onChange={handleChange} className="input mt-2" placeholder="Daily contribution" />
            </label>
            <label className="text-sm font-semibold text-ink">Amount
              <div className="relative mt-2"><span className="absolute left-4 top-3 text-sm text-slate-400">₦</span><input type="number" name="contribution_amount" min="1" step="0.01" value={form.contribution_amount} onChange={handleChange} className="input pl-8" placeholder="0.00" required /></div>
            </label>
            <label className="text-sm font-semibold text-ink">Number of units
              <input type="number" name="quantity" min="1" value={form.quantity} onChange={handleChange} className="input mt-2" />
            </label>
            <label className="text-sm font-semibold text-ink">Payment receipt <span className="font-normal text-slate-400">(optional)</span>
              <input type="file" accept="image/*,.pdf" onChange={(event) => setReceipt(event.target.files?.[0] || null)} className="input mt-2 file:mr-3 file:rounded-xl file:border-0 file:bg-sand file:px-3 file:py-1 file:text-xs file:font-semibold" />
            </label>
          </div>
          <label className="block text-sm font-semibold text-ink">Note <span className="font-normal text-slate-400">(optional)</span>
            <textarea name="note" value={form.note} onChange={handleChange} rows="3" className="input mt-2 resize-none" placeholder="Add a short payment reference" />
          </label>
          {error && <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-600">{error}</p>}
          <button type="submit" disabled={submitting} className="w-full rounded-2xl bg-brand px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand/20 disabled:opacity-60 md:w-auto">
            {submitting ? "Saving contribution..." : "Confirm contribution"}
          </button>
        </form>
      )}

      <section className="card-surface rounded-[28px] p-5 md:p-7">
        <div className="flex items-end justify-between gap-3">
          <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Activity</p><h2 className="mt-1 text-2xl font-semibold">Contribution history</h2></div>
          <span className="text-xs text-slate-400">Latest first</span>
        </div>
        <div className="mt-5 space-y-3">
          {loading && <p className="py-6 text-center text-sm text-slate-500">Loading contributions...</p>}
          {!loading && entries.map((entry) => (
            <article key={entry.id} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white/70 p-3.5 md:p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-mint/20 text-lg font-bold text-accent">↓</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">{entry.item_name || "Contribution"}</p>
                <p className="mt-0.5 text-xs text-slate-400">{formatDate(entry.created_at)} · {entry.quantity || 1} unit{Number(entry.quantity) === 1 ? "" : "s"}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-emerald-700">+{formatMoney(entry.contribution_amount)}</p>
                {entry.receipt_url && <a className="text-[11px] font-semibold text-brand" href={entry.receipt_url} target="_blank" rel="noreferrer">Receipt</a>}
              </div>
            </article>
          ))}
          {!loading && entries.length === 0 && <div className="rounded-2xl bg-sand/60 px-4 py-8 text-center"><p className="text-sm font-semibold text-ink">No contributions yet</p><p className="mt-1 text-xs text-slate-500">Your recorded daily contributions will appear here.</p></div>}
        </div>
      </section>
    </div>
  );
}
